"use client";

import { ChevronDownIcon } from "lucide-react";
import Link from "next/link";
import { useState } from "react";

import { BOOK_A_CALL_HREF } from "@/app/(marketing)/_components/nav-links";
import { Container } from "@/app/(marketing)/_components/ui/container";
import { cn } from "@/lib/utils";

interface PartnerFaq {
  question: string;
  answer: string;
}

const partnerFaqs: PartnerFaq[] = [
  {
    question: "What experts does Terac have?",
    answer:
      "Identity-verified experts across 200+ specialized domains, from engineers and clinicians to lawyers, accountants, and small-business owners, reachable across 195 countries. Every expert is screened through work-email verification, an onboarding assessment, and continuous evaluation, so your customers get real practitioners rather than crowdworkers or AI-generated profiles.",
  },
  {
    question: "Which use cases is Terac best for?",
    answer:
      "Anywhere your customers need verified human input: panel and AI-moderated interviews for UXR and market research, human data for frontier model training and evaluation, and agent-driven labor where an AI defines the job and Terac gets it done. The same primitives, opportunity, submission, attestation, and verified payout, serve all of them.",
  },
  {
    question: "How quickly do experts respond to projects?",
    answer:
      "Most projects launch in the morning and return verified data by the afternoon, with a median launch-to-result under 24 hours. Recruitment, screening, and moderation run automatically, so your customers are never waiting on supply.",
  },
  {
    question: "How does Terac ensure high-quality data?",
    answer:
      "Our AI moderator screens, interviews, and verifies experts on long-horizon tasks, catching fraud and effort-faking before results reach your customers. Hard attestations like government ID, work email, and LinkedIn act as verified filters, and you pay only on verified completion, so submissions that fail review never bill.",
  },
  {
    question: "How does Terac integrate with my product?",
    answer:
      "Through one API and our expert labor MCP. A single integration covers recruitment, screening, moderation, and payout, so you can embed verified experts inside your own product and keep the customer relationship. Your agents can also source, screen, and pay humans programmatically.",
  },
  {
    question: "How fast can I launch with Terac?",
    answer:
      "Integration partners are typically live in days, not months. Start with a guided pilot, then move to the API and MCP at your own pace. Our team works alongside yours through launch.",
  },
  {
    question: "How does partner pricing work?",
    answer:
      "Partners can resell expert recruitment to their customers or earn a revenue share on every project routed through Terac. Pricing is quality-bar based: rejected work is not billed. Talk to our team and we will model the economics for your business.",
  },
];

export default function PartnersFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="py-20">
      <Container>
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-4">
            <div className="bg-marketing-card-blue w-fit rounded-t-[10px] px-2.5 py-1.5">
              <p className="text-marketing-blue font-mono text-sm tracking-[-0.28px]">
                Partner FAQ
              </p>
            </div>
            <h2 className="text-marketing-text text-heading-32 md:text-heading-40 lg:text-heading-48 max-w-3xl text-balance">
              How you can partner with us
            </h2>
            <p className="text-marketing-text-muted max-w-xl text-base md:text-lg">
              Still have questions about partnering with Terac? Book a call and
              our team will walk you through it.
            </p>
            <Link
              href={BOOK_A_CALL_HREF}
              className="bg-marketing-blue mt-2 flex w-fit items-center gap-2 rounded-full px-5 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90"
            >
              Book a meeting
              <ArrowRightIcon />
            </Link>
          </div>

          <div className="flex flex-col">
            {partnerFaqs.map((faq, index) => (
              <div key={faq.question} className="flex flex-col">
                <button
                  onClick={() => {
                    setOpenIndex(openIndex === index ? null : index);
                  }}
                  className="flex w-full items-center justify-between gap-4 py-6 text-left"
                >
                  <span className="text-marketing-text text-base leading-[1.4] font-medium md:text-lg lg:text-xl">
                    {faq.question}
                  </span>
                  <div
                    className={cn(
                      "flex h-[18px] w-[18px] shrink-0 items-center justify-center transition-transform",
                      openIndex === index && "rotate-180",
                    )}
                  >
                    <ChevronDownIcon className="text-marketing-text h-[18px] w-[18px]" />
                  </div>
                </button>

                <div
                  className={cn(
                    "grid transition-[grid-template-rows,padding-bottom] duration-200 ease-in-out",
                    openIndex === index
                      ? "grid-rows-[1fr] pb-6"
                      : "grid-rows-[0fr]",
                  )}
                >
                  <div className="overflow-hidden">
                    <p className="text-marketing-text-muted max-w-[680px] text-base font-normal">
                      {faq.answer}
                    </p>
                  </div>
                </div>

                {index < partnerFaqs.length - 1 && (
                  <div className="bg-marketing-border h-px w-full" />
                )}
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}

function ArrowRightIcon() {
  return (
    <svg
      width="18"
      height="12"
      viewBox="0 0 18 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M1 6H17M17 6L12 1M17 6L12 11"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
